import * as React from 'react'
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT, useVisitOne } from '@/components/visitone/visitone-provider'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { StatusPill } from '@/components/ui/status-pill'
import { Skeleton } from '@/components/ui/skeleton'
import { useBelowMd } from '@/hooks/use-below-md'
import { guestsFixture, type CellTone } from '@/lib/fixtures'

export type TableState = 'loading' | 'data' | 'empty' | 'no-results' | 'error'
type SortDirection = 'asc' | 'desc'

export type TableCell = {
  primary: string
  secondary?: string
  tone?: CellTone
  /** Status pills shown after the primary text; each carries its own label. */
  pills?: { tone: CellTone; label: string }[]
}

export type TableRow = {
  id: string
  label: string
  detail?: { title: string; description: string }
  cells: Record<string, TableCell>
}

export type TableColumn = {
  key: string
  /** Already-translated header text. */
  label: string
  sortable?: boolean
  width?: string
}

export type TablePagination = {
  /** 0-based page, as the BFF returns it. */
  page: number
  totalPages: number
  onPageChange: (page: number) => void
}

export type TableMessages = {
  loadErrorTitle?: string
  loadErrorBody?: string
  retry?: string
  /** `secondary` where the page already has its primary action, such as a search or form button. */
  retryVariant?: 'primary' | 'secondary'
}

/** The feature's own empty state: already-translated copy and at most one action. */
export type TableEmpty = {
  title: string
  body?: string
  action?: React.ReactNode
}

const fixtureTotal = guestsFixture.viewTotals.arrivals

/*
 * ServerDataTable: one reusable list composition. It owns UI query state only
 * (sort, selection, density) — the feature owns data access. Real table
 * headers carry scope and aria-sort; selection persists a true server total;
 * read-only mode hides mutation controls but keeps review and detail.
 *
 * Without `rows` it renders the Guests demonstration fixture. With `rows` the
 * feature's server-ordered page is shown as given: `sortedBy` only reports the
 * server's order, selection is opt-in, the footer shows the feature's label,
 * and Previous/Next appear only when the server reports more than one page.
 *
 * `layout="stacked"` keeps the table from `md` up and, below it, shows the
 * server page as a labelled list: the first column is each row's heading
 * line, the other columns a description list, then the row's actions. Only
 * one layout is in the document at a time. `empty` replaces the generic
 * empty state with the feature's own copy and action; `noResults` does the
 * same for a search or filter that matched nothing.
 */
export function ServerDataTable({
  state = 'data',
  readOnly = false,
  onRetry,
  columns: columnsProp,
  rows: rowsProp,
  selectable,
  sortedBy,
  rowActions,
  caption,
  regionLabel,
  footer,
  pagination,
  loadingRowCount = 6,
  messages,
  layout = 'table',
  empty,
  noResults,
}: {
  state?: TableState
  readOnly?: boolean
  onRetry?: () => void
  columns?: TableColumn[]
  rows?: TableRow[]
  selectable?: boolean
  sortedBy?: { key: string; direction: SortDirection }
  /** A trailing column of per-row controls; `label` is its visually hidden header. */
  rowActions?: { label: string; render: (row: TableRow) => React.ReactNode }
  caption?: string
  regionLabel?: string
  footer?: string
  pagination?: TablePagination
  loadingRowCount?: number
  messages?: TableMessages
  /** `stacked`: rows become a list below `md` (server pages only). */
  layout?: 'table' | 'stacked'
  empty?: TableEmpty
  noResults?: TableEmpty
}) {
  const t = useT()
  const { density } = useVisitOne()
  const belowMd = useBelowMd()
  const fromServer = rowsProp !== undefined
  const columns = React.useMemo<TableColumn[]>(
    () =>
      columnsProp ??
      guestsFixture.columns.map((column) => ({
        key: column.key,
        label: t(column.labelKey),
        sortable: column.sortable,
        width: column.width,
      })),
    [columnsProp, t],
  )
  const rows = (rowsProp ?? guestsFixture.rows) as TableRow[]
  const total = fromServer ? rows.length : fixtureTotal

  const [sortKey, setSortKey] = React.useState<string>('time')
  const [sortDir, setSortDir] = React.useState<SortDirection>('asc')
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [allMatching, setAllMatching] = React.useState(false)
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set())
  const [focusedRow, setFocusedRow] = React.useState(0)

  const rowHeight = density === 'compact' ? 'var(--vo-table-row-compact)' : 'var(--vo-table-row-comfortable)'

  const sortedRows = React.useMemo(() => {
    if (fromServer) return rows
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = a.cells[sortKey].primary
      const bv = b.cells[sortKey].primary
      const compare = av.localeCompare(bv, 'en-GB', { numeric: true })
      return sortDir === 'asc' ? compare : -compare
    })
    return copy
  }, [fromServer, rows, sortKey, sortDir])

  const activeSort = fromServer ? sortedBy : { key: sortKey, direction: sortDir }

  const toggleSort = (key: string) => {
    if (key === sortKey) setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const pageIds = sortedRows.map((row) => row.id)
  const pageSelected = pageIds.every((id) => selected.has(id)) && pageIds.length > 0
  const selectionCount = allMatching ? total : selected.size

  const setPageSelection = (checked: boolean) => {
    setAllMatching(false)
    setSelected(() => (checked ? new Set(pageIds) : new Set()))
  }

  const toggleRow = (id: string) => {
    setAllMatching(false)
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const clearSelection = () => {
    setSelected(new Set())
    setAllMatching(false)
  }

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const showSelection = !readOnly && (selectable ?? !fromServer)
  const hasDetail = rows.some((row) => row.detail)
  const keyboardRows = showSelection || hasDetail

  // Keyboard row model: j/k move, x selects, Enter opens/expands, Esc clears.
  const onRegionKeyDown = (event: React.KeyboardEvent) => {
    if (!keyboardRows || sortedRows.length === 0) return
    if (event.key === 'j') {
      event.preventDefault()
      setFocusedRow((index) => Math.min(index + 1, sortedRows.length - 1))
    } else if (event.key === 'k') {
      event.preventDefault()
      setFocusedRow((index) => Math.max(index - 1, 0))
    } else if (event.key === 'x' && showSelection) {
      event.preventDefault()
      toggleRow(sortedRows[focusedRow].id)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const row = sortedRows[focusedRow]
      if (row.detail) toggleExpand(row.id)
    } else if (event.key === 'Escape') {
      clearSelection()
    }
  }

  if (state === 'empty') {
    return empty ? (
      <StateBlock title={empty.title} body={empty.body} action={empty.action} />
    ) : (
      <StateBlock title={t('core.table.noDataTitle')} body={t('core.table.noDataBody')} />
    )
  }
  if (state === 'no-results') {
    return noResults ? (
      <StateBlock title={noResults.title} body={noResults.body} action={noResults.action} />
    ) : (
      <StateBlock title={t('core.table.noResultsTitle')} body={t('core.table.noResultsBody')} />
    )
  }
  if (state === 'error') {
    return (
      <StateBlock
        tone="danger"
        icon={<TriangleAlert className="size-6" />}
        title={messages?.loadErrorTitle ?? t('core.table.loadErrorTitle')}
        body={messages?.loadErrorBody ?? t('core.table.loadErrorBody')}
        action={
          <Button variant={messages?.retryVariant ?? 'primary'} onClick={onRetry}>
            {messages?.retry ?? t('core.table.retry')}
          </Button>
        }
      />
    )
  }

  const columnCount = columns.length + (showSelection ? 1 : 0) + (hasDetail ? 1 : 0) + (rowActions ? 1 : 0)

  const serverFooter = (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-2.5">
      {footer ? <p className="text-sm text-fg-muted tabular-nums">{footer}</p> : null}
      {pagination && pagination.totalPages > 1 ? (
        <nav aria-label={t('core.table.pagination')} className="ml-auto flex items-center gap-2">
          <p className="text-sm text-fg-muted tabular-nums">{t('core.table.page', { page: pagination.page + 1 })}</p>
          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page <= 0}
            onClick={() => pagination.onPageChange(pagination.page - 1)}
          >
            {t('core.table.previousPage')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page >= pagination.totalPages - 1}
            onClick={() => pagination.onPageChange(pagination.page + 1)}
          >
            {t('core.table.nextPage')}
          </Button>
        </nav>
      ) : null}
    </div>
  )

  if (fromServer && layout === 'stacked' && belowMd) {
    const [first, ...rest] = columns
    return (
      <section
        aria-label={caption ?? t('core.table.region')}
        data-layout="stacked"
        className="overflow-hidden rounded-md border border-line bg-surface"
      >
        <ul>
          {state === 'loading'
            ? Array.from({ length: loadingRowCount }).map((_, index) => (
                <li key={index} className="flex flex-col gap-2 border-b border-line px-4 py-3 last:border-b-0">
                  <Skeleton className="h-3.5 w-40" />
                  <Skeleton className="h-3.5 w-24" />
                </li>
              ))
            : rows.map((row) => (
                <li key={row.id} className="flex flex-col gap-2 border-b border-line px-4 py-3 last:border-b-0">
                  <div className="min-w-0">
                    <span className="sr-only">{first.label}: </span>
                    <Cell cell={row.cells[first.key]} emphasize />
                  </div>
                  {rest.length ? (
                    <dl className="flex flex-col gap-1.5">
                      {rest.map((column) => (
                        <div key={column.key} className="flex flex-col gap-1">
                          <dt className="text-xs font-medium text-fg-muted">{column.label}</dt>
                          <dd className="min-w-0">
                            <Cell cell={row.cells[column.key]} />
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                  {rowActions ? (
                    <div
                      role="group"
                      aria-label={rowActions.label}
                      className="flex flex-wrap items-center gap-2"
                    >
                      {rowActions.render(row)}
                    </div>
                  ) : null}
                </li>
              ))}
        </ul>
        {serverFooter}
      </section>
    )
  }

  return (
    <div className="overflow-hidden rounded-md border border-line bg-surface">
      {showSelection && selectionCount > 0 ? (
        <div className="flex flex-wrap items-center gap-3 border-b border-line bg-selected px-4 py-2.5">
          <p aria-live="polite" className="text-sm font-medium text-fg-strong">
            {t('core.table.selected', { count: selectionCount })}
          </p>
          {pageSelected && !allMatching ? (
            <button
              type="button"
              onClick={() => setAllMatching(true)}
              className="text-sm font-medium text-action underline-offset-4 hover:underline"
            >
              {t('core.table.selectMatching', { count: total })}
            </button>
          ) : null}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" size="sm">
              {t('core.table.resendEmail')}
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              {t('core.table.clearSelection')}
            </Button>
          </div>
        </div>
      ) : null}

      <div
        role="region"
        aria-label={regionLabel ?? t('core.table.scrollRegion')}
        tabIndex={0}
        onKeyDown={onRegionKeyDown}
        className="overflow-x-auto rounded-sm focus-visible:-outline-offset-2"
      >
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{caption ?? t('core.table.region')}</caption>
          <thead>
            <tr className="border-b border-line bg-surface-subtle text-left">
              {showSelection ? (
                <th scope="col" className="w-11 px-2" style={{ height: rowHeight }}>
                  {state === 'loading' ? null : (
                    <Checkbox
                      checked={pageSelected}
                      aria-label={t('core.table.selectPage')}
                      onChange={(event) => setPageSelection(event.target.checked)}
                    />
                  )}
                </th>
              ) : null}
              {hasDetail ? <th scope="col" className="w-8" aria-hidden="true" /> : null}
              {columns.map((column) => {
                const active = activeSort?.key === column.key
                const direction = activeSort?.direction
                return (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}
                    style={{ minWidth: column.width, height: rowHeight }}
                    className="px-3.5 text-xs font-semibold tracking-wide text-fg-muted"
                  >
                    {column.sortable && !fromServer ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(column.key)}
                        className="inline-flex items-center gap-1 py-2 text-xs font-semibold text-fg-muted hover:text-fg"
                      >
                        {column.label}
                        {active ? (
                          direction === 'asc' ? (
                            <ArrowUp aria-hidden="true" className="size-3.5" />
                          ) : (
                            <ArrowDown aria-hidden="true" className="size-3.5" />
                          )
                        ) : null}
                      </button>
                    ) : (
                      <span className="inline-block py-2">{column.label}</span>
                    )}
                  </th>
                )
              })}
              {rowActions ? (
                <th scope="col" className="px-3.5 text-right">
                  <span className="sr-only">{rowActions.label}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {state === 'loading'
              ? Array.from({ length: loadingRowCount }).map((_, index) => (
                  <tr key={index} className="border-b border-line" style={{ height: rowHeight }}>
                    {showSelection ? (
                      <td className="px-2">
                        <Skeleton className="mx-auto size-[1.125rem]" />
                      </td>
                    ) : null}
                    {hasDetail ? <td /> : null}
                    {columns.map((column) => (
                      <td key={column.key} className="px-3.5">
                        <Skeleton className="h-3.5 w-24" />
                      </td>
                    ))}
                    {rowActions ? (
                      <td className="px-3.5">
                        <Skeleton className="ml-auto h-3.5 w-16" />
                      </td>
                    ) : null}
                  </tr>
                ))
              : sortedRows.map((row, index) => {
                  const isSelected = allMatching || selected.has(row.id)
                  const isExpanded = expanded.has(row.id)
                  const isFocused = keyboardRows && index === focusedRow
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        style={{ height: rowHeight }}
                        className={cn(
                          'border-b border-line transition-colors hover:bg-surface-hover',
                          isSelected && 'bg-selected',
                          isFocused && 'outline outline-2 -outline-offset-2 outline-focus',
                        )}
                      >
                        {showSelection ? (
                          <td className="px-2 align-middle">
                            <Checkbox
                              checked={isSelected}
                              aria-label={t('core.table.selectRow', { subject: row.label })}
                              onChange={() => toggleRow(row.id)}
                            />
                          </td>
                        ) : null}
                        {hasDetail ? (
                          <td className="align-middle">
                            {row.detail ? (
                              <button
                                type="button"
                                aria-expanded={isExpanded}
                                aria-label={
                                  fromServer
                                    ? t(isExpanded ? 'core.table.collapseDetail' : 'core.table.expandDetail', { subject: row.label })
                                    : t(isExpanded ? 'core.table.collapseRow' : 'core.table.expandRow', { subject: row.label })
                                }
                                onClick={() => toggleExpand(row.id)}
                                className="inline-flex size-8 items-center justify-center rounded-sm text-fg-muted hover:bg-surface-hover focus-visible:-outline-offset-2"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="size-4" />
                                ) : (
                                  <ChevronRight className="size-4" />
                                )}
                              </button>
                            ) : null}
                          </td>
                        ) : null}
                        {columns.map((column, columnIndex) => (
                          <td
                            key={column.key}
                            className="px-3.5 align-middle"
                            style={{ paddingBlock: '0.5rem' }}
                          >
                            <Cell
                              cell={row.cells[column.key]}
                              emphasize={fromServer ? columnIndex === 0 : column.key === 'name'}
                            />
                          </td>
                        ))}
                        {rowActions ? (
                          <td className="px-3.5 align-middle" style={{ paddingBlock: '0.5rem' }}>
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              {rowActions.render(row)}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                      {isExpanded && row.detail ? (
                        <tr className="border-b border-line bg-surface-subtle">
                          <td colSpan={columnCount} className="px-4 py-3">
                            <p className="text-sm font-medium text-fg-strong">{row.detail.title}</p>
                            <p className="text-sm text-fg-muted">{row.detail.description}</p>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  )
                })}
          </tbody>
        </table>
      </div>

      {fromServer ? (
        serverFooter
      ) : (
        <nav
          aria-label={t('core.table.pagination')}
          className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-2.5"
        >
          <p className="text-sm text-fg-muted tabular-nums">
            {t('core.table.range', { start: 1, end: rows.length, total })}
          </p>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" size="sm" disabled>
              {t('core.table.previousPage')}
            </Button>
            <Button variant="secondary" size="sm">
              {t('core.table.nextPage')}
            </Button>
          </div>
        </nav>
      )}
    </div>
  )
}

function Cell({ cell, emphasize }: { cell: TableCell; emphasize?: boolean }) {
  if (cell.tone) {
    return <StatusPill tone={cell.tone}>{cell.primary}</StatusPill>
  }
  const primary = (
    <span className={cn('text-sm wrap-anywhere', emphasize ? 'font-medium text-fg-strong' : 'text-fg')}>
      {cell.primary}
    </span>
  )
  return (
    <div className="flex flex-col gap-0.5">
      {cell.pills?.length ? (
        <span className="inline-flex flex-wrap items-center gap-2">
          {primary}
          {cell.pills.map((pill) => (
            <StatusPill key={pill.label} tone={pill.tone}>
              {pill.label}
            </StatusPill>
          ))}
        </span>
      ) : (
        primary
      )}
      {cell.secondary ? <span className="text-xs text-fg-muted wrap-anywhere">{cell.secondary}</span> : null}
    </div>
  )
}

function StateBlock({
  title,
  body,
  action,
  icon,
  tone = 'neutral',
}: {
  title: string
  body?: string
  action?: React.ReactNode
  icon?: React.ReactNode
  tone?: 'neutral' | 'danger'
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className="flex flex-col items-center gap-3 rounded-md border border-line bg-surface px-6 py-14 text-center"
    >
      {icon ? (
        <span aria-hidden="true" className={cn('inline-flex', tone === 'danger' ? 'text-danger' : 'text-fg-muted')}>
          {icon}
        </span>
      ) : null}
      <div className="max-w-md">
        <p className="text-lg text-fg-strong">{title}</p>
        {body ? <p className="mt-1 text-sm text-fg-muted">{body}</p> : null}
      </div>
      {action}
    </div>
  )
}
